/**
 * 「**隐藏不常用的音**」的**界面侧**纯函数（不碰 React、不碰网络）。
 *
 * ## 2026-10-18：从「本机记忆」改成「**服务端为唯一来源**」（用户原话）
 *
 * 上一版这一层是 `localStorage`（`fht.dictation.hiddenReadings.<hanziId>`），
 * 理由是"本轮尽量不加迁移"。用户看到之后的答复是：
 *
 * > "**第一个，隐藏读音要存服务器。**"
 *
 * 于是现在：
 *
 * | 谁 | 怎么拿 / 怎么改 |
 * | --- | --- |
 * | 读（家长 + 管理员） | `GET .../hanzi/:id/readings` → `hiddenReadings`（+ 列表 DTO 里也带一份） |
 * | 写（**只有管理员**） | `PUT /admin/dictation/hanzi/:id/hidden-readings`（审计 + 校验） |
 * | 本机存储 | **不再读写** —— 只剩一个"清理旧键"的动作（见下） |
 *
 * ⛔ **不要**在这里恢复任何 `getItem` / `setItem`：两套来源并存时，
 *    "界面显示的是本机那份、别的设备看到的是服务端那份"这种 bug 无法解释，
 *    而且家长端永远看不到管理员藏了什么。
 *
 * ## 旧 `localStorage` 的数据怎么处理（向后兼容）
 *
 * 用户要求"已存的旧值不要报错"。做法是：**完全忽略它们**（一个新的都不读），
 * 并把这个设备上遗留的旧键**一次性清掉**（`purgeLegacyHiddenReadings()`），
 * 让 `localStorage` 里不再留一份"看起来像真相"的副本。清理失败（隐私模式 /
 * 被策略禁用 / 存储为 `null`）就**静默跳过** —— 那本来就不是数据。
 *
 * ## 这个文件现在只剩两件纯逻辑
 *
 *   · `toggleHiddenReading()` —— "点了这个音"→ 下一份隐藏集合（给接口当入参）；
 *   · `purgeLegacyHiddenReadings()` —— 清掉旧键（返回清了几条，用于提示一句
 *     "已改为云端保存"）。
 *
 * `splitReadingsByHidden()` 已经搬去 `@fht/shared`（后端与前端共用同一份切分口径）。
 */
import { MAX_HIDDEN_READINGS } from '@fht/shared';

/**
 * **旧** localStorage 的键前缀（只有清理时还认它）。
 *
 * ⚠️ 保留这个常量是为了"能清掉它"，不是为了再读它。
 */
export const HIDDEN_READINGS_STORAGE_PREFIX = 'fht.dictation.hiddenReadings.';

/**
 * 一个字最多记几个隐藏读音。
 *
 * ⚠️ 单一来源在 `@fht/shared`：zod、服务层与界面引的都是那一份，
 *    这里只是**转出去**方便老的调用点与单测继续从本模块取。
 */
export { MAX_HIDDEN_READINGS };

/**
 * 清理旧键所需的最小存储接口（`localStorage` 天然满足）。
 *
 * ⚠️ 只用到这三样：遍历（`length` + `key(i)`）与删除（`removeItem`）。
 *    传内存替身就能测（本项目没有 jsdom）。
 */
export interface LegacyHiddenReadingsStorage {
  readonly length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
}

/**
 * 默认存储 = `localStorage`。
 *
 * ⚠️ 全部包在 try/catch 里：`localStorage` 在**取属性**时就可能抛
 *    （Safari 隐私模式、被策略禁用、SSR 环境），抛了就当"没有存储"。
 */
export function defaultLegacyStorage(): LegacyHiddenReadingsStorage | null {
  try {
    const storage = globalThis.localStorage;
    if (storage === undefined || storage === null) return null;
    return storage;
  } catch {
    return null;
  }
}

/**
 * 把这个设备上**旧版**留下的隐藏读音记录一次性清掉。
 *
 * @returns 清掉的键数（`0` = 本来就没有旧记录 / 存储不可用）。
 *
 * ⚠️ 这是**唯一**还会碰旧键的地方。清完之后界面/接口都只认服务端。
 * ⚠️ **先把要删的键收集齐、再删**：`localStorage` 的索引会在删除时前移，
 *    边遍历边删会漏掉一半（这是这类清理最常见的 bug）。
 * ⚠️ 单个键删失败（被策略禁用）就跳过它，绝不因此让整页报错。
 */
export function purgeLegacyHiddenReadings(
  storage: LegacyHiddenReadingsStorage | null = defaultLegacyStorage(),
): number {
  if (storage === null) return 0;
  const doomed: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && key.startsWith(HIDDEN_READINGS_STORAGE_PREFIX)) doomed.push(key);
    }
  } catch {
    return 0;
  }
  let removed = 0;
  for (const key of doomed) {
    try {
      storage.removeItem(key);
      removed += 1;
    } catch {
      /* 删不掉就算了：它不再被读取，留着也不影响任何行为 */
    }
  }
  return removed;
}

/**
 * 把一个读音在"隐藏 / 显示"之间切换 —— **纯函数**，结果是给接口的下一份入参。
 *
 * ⚠️ 返回**新数组**（不改入参）：调用方拿它去 `PUT .../hidden-readings`。
 * ⚠️ 上限与后端同一个常量（`MAX_HIDDEN_READINGS`）；到上限时**不再加**
 *    （界面上那一个按钮本来就只在这几个音里点，正常永远到不了上限）。
 * ⚠️ 空串 / 空白**不产生任何变化**（"隐藏一个空音"没有意义）。
 */
export function toggleHiddenReading(
  readings: readonly string[],
  reading: string,
): string[] {
  const value = reading.trim();
  if (value === '') return [...readings];
  if (readings.includes(value)) return readings.filter((item) => item !== value);
  if (readings.length >= MAX_HIDDEN_READINGS) return [...readings];
  return [...readings, value];
}
